/* Munne product page.
 *
 * Gallery dots for the mobile slider. Dawn's slider-component already tracks
 * the current slide and dispatches `slideChanged`; this only mirrors that
 * state into a row of dots and scrolls the slider when a dot is tapped.
 *
 * And, at the end of the file, colour switching that stays out of the
 * browser history, the size guide's bottom sheet being dragged closed, and
 * the zoom modal's thumbnails.
 */

class MunneGalleryDots extends HTMLElement {
  connectedCallback() {
    this.sliderComponent = this.closest('slider-component');
    if (!this.sliderComponent) return;

    this.slider = this.sliderComponent.querySelector('[id^="Slider-"]');
    this.slides = Array.from(this.sliderComponent.querySelectorAll('[id^="Slide-"]'));
    if (!this.slider || this.slides.length < 2) {
      this.hidden = true;
      return;
    }

    this.render();
    this.sliderComponent.addEventListener('slideChanged', (event) => {
      this.setCurrent(event.detail.currentPage);
    });
  }

  render() {
    const label = this.dataset.label || '';
    this.innerHTML = '';
    this.dots = this.slides.map((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'munne-gallery-dots__dot';
      dot.setAttribute('aria-label', `${label} ${index + 1}`.trim());
      dot.addEventListener('click', () => this.goTo(index));
      this.appendChild(dot);
      return dot;
    });
    this.setCurrent(1);
  }

  setCurrent(page) {
    this.dots.forEach((dot, index) => {
      if (index === page - 1) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  goTo(index) {
    const target = this.slides[index];
    if (!target) return;
    this.slider.scrollTo({
      left: target.offsetLeft - this.slides[0].offsetLeft,
      behavior: 'smooth',
    });
  }
}

customElements.define('munne-gallery-dots', MunneGalleryDots);

/* Colour swatches without history entries.
 *
 * At Munne each colour is its own product, so a swatch is a plain link to
 * another URL, and every colour tried used to become a step of Back. The
 * click is turned into location.replace() instead: the page loads as before,
 * but in place of the current entry, so Back leaves the product for wherever
 * the shopper came from (the collection, the search) rather than walking
 * back through the colours.
 *
 * The swatches stay real links, so crawlers, analytics and no-JS browsing are
 * unchanged, and anything asking for a new tab or window (a modifier key, the
 * middle button) is left to the browser.
 */
document.addEventListener('click', (event) => {
  const swatch = event.target.closest('.munne-colors__swatch');
  if (!swatch || !swatch.href) return;
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  event.preventDefault();
  // The colour on screen: nothing to load.
  if (swatch.getAttribute('aria-current') === 'page') return;
  window.location.replace(swatch.href);
});

/* The size guide's bottom sheet (mobile): drag it down by its handle to
 * close. Past a quarter of its height, or flicked, it goes through Dawn's
 * own modal-dialog hide(); otherwise it springs back. While dragged,
 * .is-dragging turns the transition off and the pointer writes the
 * transform; letting go hands it back to the CSS, so it slides from where it
 * was released. The handle has touch-action: none, so pointer events cover
 * finger and mouse alike.
 */
{
  let drag = null;

  document.addEventListener('pointerdown', (event) => {
    const handle = event.target.closest('.munne-size-guide__handle');
    if (!handle) return;
    const dialog = handle.closest('.munne-size-guide__dialog');
    handle.setPointerCapture(event.pointerId);
    dialog.classList.add('is-dragging');
    drag = { handle, dialog, startY: event.clientY, startTime: event.timeStamp, offset: 0 };
  });

  document.addEventListener('pointermove', (event) => {
    if (!drag) return;
    drag.offset = Math.max(0, event.clientY - drag.startY);
    drag.dialog.style.transform = `translateY(${drag.offset}px)`;
  });

  const release = (event) => {
    if (!drag) return;
    const { dialog, offset, startTime } = drag;
    drag = null;

    const speed = offset / Math.max(1, event.timeStamp - startTime);
    const dismiss = offset > dialog.offsetHeight * 0.25 || speed > 0.5;

    dialog.classList.remove('is-dragging');
    dialog.style.transform = '';
    if (dismiss) dialog.closest('modal-dialog')?.hide();
  };

  document.addEventListener('pointerup', release);
  document.addEventListener('pointercancel', release);
}

/* The zoom modal's thumbnails (desktop, snippets/product-media-modal.liquid).
 * A click scrolls the modal to that photo; while scrolling, the thumbnail of
 * the photo most in view is marked current. Dawn already jumps to the photo
 * that was clicked when the modal opens, so the observer marks the right
 * thumbnail then too.
 */
document.querySelectorAll('.product-media-modal').forEach((modal) => {
  const content = modal.querySelector('.product-media-modal__content');
  const thumbs = Array.from(modal.querySelectorAll('[data-munne-zoom-target]'));
  if (!content || thumbs.length === 0) return;

  const photoFor = (thumb) => content.querySelector(`[data-media-id="${thumb.dataset.munneZoomTarget}"]`);

  // Dawn's media modal closes on any mouse pointerup inside it (global.js,
  // ModalDialog), and the thumbnails are inside it.
  modal.querySelector('.munne-zoom-thumbs')?.addEventListener('pointerup', (event) => event.stopPropagation());

  const mark = (id) => {
    thumbs.forEach((thumb) => {
      thumb.setAttribute('aria-current', thumb.dataset.munneZoomTarget === id ? 'true' : 'false');
    });
  };

  modal.addEventListener('click', (event) => {
    const thumb = event.target.closest('[data-munne-zoom-target]');
    if (!thumb) return;
    const photo = photoFor(thumb);
    if (!photo) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    photo.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    mark(thumb.dataset.munneZoomTarget);
  });

  // The share of each photo in view; the largest one is current.
  const visible = new Map();
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => visible.set(entry.target.dataset.mediaId, entry.intersectionRatio));
      let best = null;
      visible.forEach((ratio, id) => {
        if (best === null || ratio > visible.get(best)) best = id;
      });
      if (best !== null && visible.get(best) > 0) mark(best);
    },
    { root: content, threshold: [0, 0.25, 0.5, 0.75, 1] }
  );
  thumbs.forEach((thumb) => {
    const photo = photoFor(thumb);
    if (photo) observer.observe(photo);
  });
});
